import { useState } from "react";
import { createPortal } from "react-dom";
import { Award, Eye, Printer, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useApi, fmtDate } from "../../lib/api";
import { Stagger, AnimatedCard } from "../../components/motion";
import { PageHeader, SkeletonGrid, EmptyState, ErrorState, Badge, Logo } from "../../components/common";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";

export const verifyUrl = (n) => `${window.location.origin}/dogrula/${encodeURIComponent(n)}`;

export function PrintButton({ c }) {
  return (
    <>
      <button onClick={() => window.print()} className="btn-primary no-print inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold" data-testid="certificate-print-btn">
        <Printer className="h-4 w-4" /> Yazdır / PDF Olarak Kaydet
      </button>
      {createPortal(<div className="print-only"><CertView c={c} /></div>, document.body)}
    </>
  );
}

export function CertView({ c }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white p-8 sm:p-12 text-center ring-1 ring-slate-200" data-testid="certificate-view">
      <div className="absolute inset-3 rounded-xl border border-[rgba(227,10,23,0.25)]" />
      <div className="absolute inset-5 rounded-lg border border-dashed border-slate-200" />
      <div className="relative">
        <Logo className="mx-auto h-20 w-auto" />
        <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[var(--brand)]">Başarı Sertifikası</p>
        <p className="mt-6 text-sm text-slate-500">Bu belge,</p>
        <p className="mt-1 text-3xl font-extrabold tracking-tight">{c.student_name}</p>
        <p className="mt-3 text-sm text-slate-500">adlı kursiyerin aşağıdaki eğitim programını başarıyla tamamladığını gösterir.</p>
        <p className="mt-4 text-lg font-bold text-[var(--ink)]">{c.course_title}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-8 text-left text-xs">
          <div><p className="text-slate-400">Sertifika No</p><p className="font-mono font-semibold">{c.number}</p></div>
          <div><p className="text-slate-400">Düzenleme Tarihi</p><p className="font-semibold">{fmtDate(c.issue_date)}</p></div>
        </div>
        <div className="mt-8 flex items-center justify-center gap-4 text-left">
          <QRCodeSVG value={verifyUrl(c.number)} size={84} level="M" fgColor="#0f172a" data-testid="certificate-qr" />
          <div className="max-w-[220px] text-[11px] leading-relaxed text-slate-500">
            <p className="flex items-center gap-1 font-semibold text-[var(--ink)]"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Doğrulanabilir Sertifika</p>
            <p>QR kodu okutarak veya {window.location.host}/dogrula adresinden doğrulayabilirsiniz.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Certificates() {
  const { data, loading, error, reload } = useApi("/me/certificates");
  const [open, setOpen] = useState(null);
  return (
    <div>
      <PageHeader eyebrow="Başarılarım" title="Sertifikalarım" desc="Tamamladığınız eğitimlere ait sertifikalar." />
      {error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : loading ? (
        <SkeletonGrid count={2} className="h-60" cols="md:grid-cols-2" />
      ) : data.length === 0 ? (
        <EmptyState icon={Award} title="Henüz bir sertifikanız bulunmuyor." desc="Eğitiminizi tamamladıktan sonra sertifikanız burada görüntülenecektir." testid="certificates-empty" />
      ) : (
        <Stagger className="grid gap-5 md:grid-cols-2" gap={0.08}>
          {data.map((c) => (
            <AnimatedCard key={c.id} className="gradient-border group relative overflow-hidden p-6" data-testid={`certificate-card-${c.id}`}>
              <div className="orb -right-16 -top-16 h-48 w-48 transition-opacity duration-500 group-hover:opacity-80" style={{ background: "rgba(227,10,23,0.14)" }} />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <Logo variant="mark" className="h-9 w-auto" />
                  <Badge status={c.status} />
                </div>
                <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Sertifika</p>
                <h3 className="mt-1 text-lg font-bold text-[var(--ink)]">{c.course_title}</h3>
                <p className="text-sm text-slate-500">{c.student_name}</p>
                <div className="mt-5 flex items-end justify-between gap-3 border-t border-slate-100 pt-4">
                  <div>
                    <p className="font-mono text-sm font-semibold text-[var(--ink)]" data-testid="certificate-number">{c.number}</p>
                    <p className="text-xs text-slate-400">{fmtDate(c.issue_date)}</p>
                  </div>
                  <button onClick={() => setOpen(c)} className="btn-ghost inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold" data-testid={`certificate-view-btn-${c.id}`}>
                    <Eye className="h-4 w-4" /> Sertifikayı Görüntüle
                  </button>
                </div>
              </div>
            </AnimatedCard>
          ))}
        </Stagger>
      )}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl rounded-2xl p-3 sm:p-4">
          <DialogTitle className="sr-only">Sertifika</DialogTitle>
          {open && (
            <>
              <CertView c={open} />
              <div className="no-print flex justify-end pt-1"><PrintButton c={open} /></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
